import Avatar from "@mui/material/Avatar";
import Backdrop from "@mui/material/Backdrop";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import CssBaseline from "@mui/material/CssBaseline";
import Grid from "@mui/material/Grid";
import Link from "@mui/material/Link";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useMutation, useQuery } from "@tanstack/react-query";
import type { AxiosError } from "axios";
import type { ChangeEvent, FormEvent } from "react";
import { useCallback, useEffect, useState } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { IconButton, Skeleton, Stack, useTheme } from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import Icon from "@mdi/react";
import { mdiLoginVariant } from "@mdi/js";
import { useAuthenticationContext } from "../../../hooks/context/useAuthenticationContext";
import { authenticationService } from "../../../services/authentication";
import { CognitoErrorMessage, type CustomCognitoUser, type SignInParameters } from "../../../services/authentication/types";
import { useBackendServices } from "../../../services/backend";
import type { Subdomain } from "../../../services/backend/types";
import {
  buildHomePageUrl,
  buildResetPasswordPageUrl,
  buildSignUpPageUrl,
  buildSupportPageUrl,
  buildTeamsPageUrl,
  buildVerifyEmailAddressPageUrl,
} from "../../../utils/BuildURLs";
import { SCHOOL_BACKGROUND_IMAGE, DEFAULT_SCHOOL_LOGO_URL } from "../../../utils/Constants";
import { pagePaths } from "../../../constants/pagePaths";
import { borderRadiusStandard } from "../../../theme";
import { ToastError500 } from "../../../utils/UI/ToastError500";
import { decryptData } from "../../../utils/SupportAccess";

const logImageStyle = {
  margin: 0,
  width: "100%",
  height: "100%",
  background: "white",
  objectFit: "contain",
} as const;

export const SignInPage = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  const { setUser, setUserId, setSchoolId, setRole, setDistributorId } = useAuthenticationContext();
  const [searchParameters] = useSearchParams();
  const initialEmail = searchParameters.get("email");
  const passwordURL = searchParameters.get("password");
  const logFederationUser = searchParameters.get("logFederationUser");
  const [emailAddress, setEmailAddress] = useState(initialEmail ?? "");
  const [password, setPassword] = useState("");
  const [subdomain, setSubdomain] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { backendService } = useBackendServices();
  const [backgroundImage, setBackgroundImage] = useState<string>(SCHOOL_BACKGROUND_IMAGE);
  const [logoImage, setLogoImage] = useState<string>(DEFAULT_SCHOOL_LOGO_URL);
  const [schoolName, setSchoolName] = useState<string | undefined>(undefined);

  const { mutate: signIn, isLoading: isSigningIn } = useMutation<CustomCognitoUser, Error, SignInParameters>(({ emailAddress, password }: SignInParameters) =>
    authenticationService.signIn({ emailAddress, password }),
  );

  const handleGoogleSignIn = async () => {
    authenticationService.signInWithGoogle();
  };

  const GoogleIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48" width="24px" height="24px">
      <path
        fill="#FFC107"
        d="M43.611,20.083H42V20H24v8h11.303c-1.649,4.657-6.08,8-11.303,8c-6.627,0-12-5.373-12-12c0-6.627,5.373-12,12-12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C12.955,4,4,12.955,4,24c0,11.045,8.955,20,20,20c11.045,0,20-8.955,20-20C44,22.659,43.862,21.35,43.611,20.083z"
      />
      <path
        fill="#FF3D00"
        d="M6.306,14.691l6.571,4.819C14.655,15.108,18.961,12,24,12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C16.318,4,9.656,8.337,6.306,14.691z"
      />
      <path
        fill="#4CAF50"
        d="M24,44c5.166,0,9.86-1.977,13.409-5.192l-6.19-5.238C29.211,35.091,26.715,36,24,36c-5.202,0-9.619-3.317-11.283-7.946l-6.522,5.025C9.505,39.556,16.227,44,24,44z"
      />
      <path
        fill="#1976D2"
        d="M43.611,20.083H42V20H24v8h11.303c-0.792,2.237-2.231,4.166-4.087,5.571c0.001-0.001,0.002-0.001,0.003-0.002l6.19,5.238C36.971,39.205,44,34,44,24C44,22.659,43.862,21.35,43.611,20.083z"
      />
    </svg>
  );

  const getSubdomainInfoQuery = useQuery<Subdomain, AxiosError>({
    queryKey: ["getSubdomainInfo", subdomain],
    queryFn: () => backendService.getSubdomain({ Name: subdomain }),
    retry: false,
    enabled: subdomain !== "",
  });

  useEffect(() => {
    const parsedData = window.location.host.split(".");
    if (parsedData.length >= 3 || parsedData[1] === "localhost:3000") {
      const subdomain = parsedData[0];
      if (subdomain !== "www") {
        setSubdomain(subdomain);
      }
    }
  }, []);

  useEffect(() => {
    if (getSubdomainInfoQuery.data) {
      if (getSubdomainInfoQuery.data.Background) {
        setBackgroundImage(getSubdomainInfoQuery.data.Background);
      }
      if (getSubdomainInfoQuery.data.Logo) {
        setLogoImage(getSubdomainInfoQuery.data.Logo);
      }
      if (getSubdomainInfoQuery.data.SchoolName) {
        setSchoolName(getSubdomainInfoQuery.data.SchoolName);
      }
    }
  }, [getSubdomainInfoQuery.data]);

  const handleSubmit = useCallback(
    (event?: FormEvent<HTMLFormElement>) => {
      event?.preventDefault();
      setLoading(true);
      signIn(
        { emailAddress, password },
        {
          onSuccess: (user: CustomCognitoUser) => {
            if (user.challengeName === "NEW_PASSWORD_REQUIRED") {
              navigate(pagePaths.updatePassword, {
                state: {
                  emailAddress: emailAddress,
                },
              });
            } else {
              if (user.getSignInUserSession()!.getAccessToken().payload["cognito:groups"]?.includes("Support")) {
                const supportPage = buildSupportPageUrl();
                navigate(supportPage);
                // TODO: Fix this properly and clean up Context when sign in a new user
                window.location.reload();
                return;
              }
              setUser(user);
              setDistributorId(user.attributes["custom:DistributorId"]);
              setUserId(user.attributes["custom:UserId"]);
              setSchoolId(user.attributes["custom:SchoolId"]);
              setRole(user.attributes["custom:Role"]);
              const homePageUrl = buildHomePageUrl();
              const distributorPage = buildTeamsPageUrl();
              if (window.location.pathname.includes("distributor")) {
                navigate(distributorPage);
              } else {
                navigate(homePageUrl);
                // TODO: Fix this properly and clean up Context when sign in a new user
                window.location.reload();
              }
            }
          },
          onError: (error: Error) => {
            if (error.name === "InternalServerException") {
              toast(<ToastError500 />);
            }
            if (error.name === "UserNotConfirmedException") {
              const verifyEmailAddressPageUrl = buildVerifyEmailAddressPageUrl({
                emailAddress,
              });
              navigate(verifyEmailAddressPageUrl);
            } else if (error.name === "NotAuthorizedException") {
              if (error.message === CognitoErrorMessage.USER_DISABLED) {
                toast.error(t("authentication.sign_in_page.user_is_disabled"));
              } else {
                toast.error(t("authentication.sign_in_page.unauthorized_exception"));
              }
            } else if (error.name === "UserNotFoundException") {
              toast.error(t("authentication.sign_in_page.user_not_found_exception"));
            }
          },
          onSettled() {
            setLoading(false);
          },
        },
      );
    },
    [emailAddress, password, navigate, setUser, setDistributorId, setUserId, setSchoolId, setRole, signIn, t],
  );

  const handleEmailChange = (event: ChangeEvent<HTMLInputElement>) => {
    event.preventDefault();
    setEmailAddress(event.target.value.trimStart().trimEnd());
  };

  const handlePasswordChange = (event: ChangeEvent<HTMLInputElement>) => {
    event.preventDefault();
    setPassword(event.target.value.trimStart().trimEnd());
  };

  const decryptPassword = async (passwordURL: string): Promise<string> => {
    const decryptedData = await decryptData(passwordURL);
    return decryptedData;
  };

  useEffect(() => {
    const decryptAndSetPassword = async () => {
      if (passwordURL) {
        const decryptedPassword = await decryptPassword(passwordURL);
        setPassword(decryptedPassword);
      }
    };
    decryptAndSetPassword();
  }, [passwordURL]);

  useEffect(() => {
    if (initialEmail && passwordURL) {
      handleSubmit();
    }
  }, [handleSubmit, initialEmail, passwordURL]);

  useEffect(() => {
    if (logFederationUser && logFederationUser === "google") {
      handleGoogleSignIn();
    }
  }, [logFederationUser]);

  const LogoImage = ({ src, alt }: { src: string; alt: string }) => <img src={src} alt={alt} style={logImageStyle} loading="eager" decoding="async" />;

  useEffect(() => {
    const link = document.createElement("link");
    link.rel = "preload";
    link.as = "image";
    link.href = backgroundImage;
    document.head.appendChild(link);
    return () => {
      document.head.removeChild(link);
    };
  }, [backgroundImage]);

  return subdomain !== "" && getSubdomainInfoQuery.isFetching ? (
    <Backdrop sx={{ color: "white" }} open={getSubdomainInfoQuery.isFetching}>
      <CircularProgress color="inherit" />{" "}
    </Backdrop>
  ) : (
    <Grid container component="main" sx={{ height: "100vh" }}>
      <CssBaseline />
      <Grid
        item
        xs={12}
        sx={{
          backgroundImage: `url(${backgroundImage})`,
          backgroundSize: "cover",
          backgroundRepeat: "no-repeat",
          backgroundColor: (t) => (t.palette.mode === "dark" ? t.palette.grey[50] : t.palette.grey[900]),
          backgroundPosition: "center",
          overflowY: "auto",
        }}
      >
        <Grid container>
          <Grid item xs={2} sm={0} />
          <Grid item xs={12} sm={8} sx={{ mt: "50px", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
            <Avatar
              sx={{
                width: 120,
                height: 120,
                m: 1,
                border: 5,
                borderColor: "white",
                bgcolor: "secondary.main",
                overflow: "hidden",
              }}
            >
              {getSubdomainInfoQuery.isFetching ? (
                <Skeleton variant="rectangular" width="100%" height="100%" animation="wave" />
              ) : (
                <LogoImage src={logoImage || DEFAULT_SCHOOL_LOGO_URL} alt="School logo" />
              )}
            </Avatar>
            <Typography component="h1" variant="h4" sx={{ color: "white", mb: 3 }} fontWeight="700">
              {schoolName || t("authentication.sign_in_page.sign_in_title")}
            </Typography>
            <Box
              component="form"
              onSubmit={handleSubmit}
              sx={{ p: 3, borderRadius: borderRadiusStandard, background: "white", textAlign: "center", width: "65%" }}
            >
              <Typography component="h1" variant="h4" fontWeight="700" mb={3}>
                {t("authentication.sign_in_page.platform_access")}
              </Typography>
              <TextField
                margin="normal"
                required
                fullWidth
                id="email"
                label={t("authentication.sign_in_page.email_address")}
                name="email"
                autoComplete="email"
                autoFocus
                onChange={handleEmailChange}
                disabled={isSigningIn}
                sx={{ ".MuiInputBase-root": { borderRadius: borderRadiusStandard } }}
              />
              <TextField
                margin="normal"
                required
                fullWidth
                name="password"
                label={t("authentication.sign_in_page.password")}
                type={showPassword ? "text" : "password"}
                id="password"
                autoComplete="current-password"
                onChange={handlePasswordChange}
                disabled={isSigningIn}
                InputProps={{
                  endAdornment: (
                    <IconButton
                      edge="end"
                      aria-label="current password"
                      onClick={() => setShowPassword(!showPassword)}
                      onMouseDown={() => setShowPassword(true)}
                    >
                      <VisibilityIcon />
                    </IconButton>
                  ),
                }}
                sx={{ ".MuiInputBase-root": { borderRadius: borderRadiusStandard } }}
              />
              <Stack direction="column" justifyContent="center" alignItems="center">
                <Button
                  id="signIn"
                  type="submit"
                  fullWidth
                  variant="contained"
                  disabled={isSigningIn}
                  onClick={(event) => {
                    event.currentTarget?.form?.current?.reportValidity();
                  }}
                  startIcon={<Icon path={mdiLoginVariant} size={1} />}
                  sx={{
                    ":hover": {
                      backgroundColor: theme.palette.ylecolor.main,
                      color: "white",
                    },
                    mt: 3,
                    mb: 1,
                    borderRadius: borderRadiusStandard,
                    backgroundColor: theme.palette.ylecolor.main,
                  }}
                >
                  {t("authentication.sign_in_page.sign_in_button")}
                </Button>
                <Typography component="h1" variant="body1" fontWeight="700" mb={1}>
                  {t("authentication.sign_in_page.or")}
                </Typography>
                <Button
                  id="googleSignIn"
                  variant="outlined"
                  fullWidth
                  onClick={handleGoogleSignIn}
                  startIcon={<GoogleIcon />}
                  sx={{
                    mb: 2,
                    textTransform: "none",
                    backgroundColor: "white",
                    color: "rgba(0, 0, 0, 0.87)",
                    borderWidth: "2px",
                    borderColor: "rgba(0, 0, 0, 0.12)",
                    borderRadius: borderRadiusStandard,
                    fontFamily: "Roboto, sans-serif",
                    fontWeight: 500,
                    "&:hover": {
                      backgroundColor: "#f1f3f4",
                      borderColor: "rgba(0, 0, 0, 0.12)",
                      boxShadow: "none",
                    },
                    height: "40px",
                    padding: "0 12px",
                  }}
                >
                  {t("authentication.sign_in_page.sign_in_with_google")}
                </Button>
              </Stack>
              <Backdrop sx={{ color: "white", zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loading}>
                <CircularProgress color="inherit" />
              </Backdrop>
              {process.env.REACT_APP_SHOW_SIGN_UP === "true" && (
                <Grid container>
                  <Grid item>
                    <Link href={buildSignUpPageUrl()} variant="body2">
                      {t("authentication.sign_in_page.sign_up")}
                    </Link>
                  </Grid>
                </Grid>
              )}
            </Box>
            <Box width="65%" sx={{ mb: 8 }}>
              <Button
                id="resetPassword"
                type="submit"
                fullWidth
                disabled={isSigningIn}
                onClick={() => navigate(buildResetPasswordPageUrl({ emailAddress }))}
                sx={{
                  mt: 2,
                  mb: 2,
                  borderRadius: borderRadiusStandard,
                  backgroundColor: "white",
                  color: theme.palette.ylecolor.main,
                  ":hover": {
                    backgroundColor: theme.palette.ylecolor.main,
                    color: "white",
                  },
                }}
              >
                {t("authentication.sign_in_page.forgot_password")}
              </Button>
            </Box>
          </Grid>
          <Grid item xs={2} sm={0} />
        </Grid>
      </Grid>
    </Grid>
  );
};
